/* FIS enhancement layer. Shopify owns subscription consent, checkout, stock and cart writes.
   This file NEVER intercepts newsletter/customer form submissions or calls Klaviyo APIs. */
(() => {
  'use strict';
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let cartRequest = null;
  function readCart() {
    if (!cartRequest) {
      const root = window.Shopify?.routes?.root || '/';
      cartRequest = fetch(`${root}cart.js`, {credentials: 'same-origin', headers: {'Accept': 'application/json'}})
        .then(r => { if (!r.ok) throw new Error('Cart could not be read'); return r.json(); })
        .finally(() => { cartRequest = null; });
    }
    return cartRequest;
  }
  function subscribeCart(handler) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS.cartUpdate, handler);
    }
    return null;
  }
  define('fis-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fis-track]');
      if (!this.track) return;
      this.abort = new AbortController();
      this.prev = this.querySelector('[data-fis-prev]');
      this.next = this.querySelector('[data-fis-next]');
      const opts = {signal: this.abort.signal};
      this.prev?.addEventListener('click', () => this.move(-1), opts);
      this.next?.addEventListener('click', () => this.move(1), opts);
      this.track.addEventListener('scroll', () => this.update(), {...opts, passive:true});
      this.track.addEventListener('keydown', e => {
        if (e.target !== this.track || !['ArrowLeft','ArrowRight'].includes(e.key)) return;
        e.preventDefault(); this.move(e.key === 'ArrowLeft' ? -1 : 1);
      }, opts);
      this.resize = new ResizeObserver(() => this.update()); this.resize.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
    move(dir) {
      const child = this.track.firstElementChild;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.track.scrollBy({left:dir * ((child?.getBoundingClientRect().width || 250) + gap), behavior:reduceMotion() ? 'auto' : 'smooth'});
    }
    update() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.prev) this.prev.disabled = this.track.scrollLeft < 3;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 3;
    }
  });
  define('fis-menu', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog'); this.opener = this.querySelector('[data-open]');
      if (!this.dialog || !this.opener) return;
      const opts={signal:this.abort.signal};
      this.opener.addEventListener('click', () => { this.dialog.showModal(); this.opener.setAttribute('aria-expanded','true'); this.oldOverflow=document.body.style.overflow; document.body.style.overflow='hidden'; },opts);
      this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close(),opts);
      this.dialog.addEventListener('close', () => { this.opener.setAttribute('aria-expanded','false'); document.body.style.overflow=this.oldOverflow||''; this.opener.focus(); },opts);
      this.dialog.addEventListener('click',e=>{ if(e.target!==this.dialog)return; const r=this.dialog.getBoundingClientRect(); if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)this.dialog.close(); },opts);
    }
    disconnectedCallback(){if(this.dialog?.open){this.dialog.close();document.body.style.overflow=this.oldOverflow||'';}this.abort?.abort();}
  });
  define('fis-shipping', class extends HTMLElement {
    connectedCallback() {
      this.unsubscribe = subscribeCart(() => this.refresh());
      this.threshold = Number(this.dataset.threshold); this.currency = this.dataset.currency;
      this.initialTotal = Number(this.dataset.total);
      this.paint(this.initialTotal);
    }
    disconnectedCallback() { this.unsubscribe?.(); }
    refresh() { readCart().then(cart=>{if(this.isConnected&&cart.currency===this.currency)this.paint(cart.total_price);}).catch(()=>{}); }
    paint(total) {
      if (!(this.threshold>0) || !Number.isFinite(total)) return;
      const remain = Math.max(0, this.threshold-total);
      const msg=this.querySelector('[data-shipping-message]'); const fill=this.querySelector('[data-shipping-fill]');
      const money=new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency:this.currency}).format(remain/100);
      if(msg)msg.textContent=remain>0?`${money} away from the free-shipping threshold.`:'Your bag meets the free-shipping threshold.';
      if(fill)fill.style.width=`${Math.max(0,Math.min(100,total/this.threshold*100))}%`;
    }
  });
  define('fis-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.info = document.getElementById(`MainProduct-${this.dataset.productSection}`);
      // Never initialize the page-wide bar in a quick-add or other dialog.
      if (!this.info || this.closest('quick-add-modal, [role="dialog"]')) return;
      this.action = this.querySelector('[data-sticky-add]');
      this.price = this.querySelector('[data-sticky-price]');
      this.image = this.querySelector('[data-sticky-image]');
      this.imageBox = this.querySelector('[data-sticky-media]');
      this.option = this.querySelector('[data-sticky-option]');
      if (!this.action) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.action.addEventListener('click', () => this.purchase(), opts);
      this.info.addEventListener('change', () => this.queueUpdate(), opts);
      this.observer = new MutationObserver(() => this.queueUpdate());
      this.observer.observe(this.info, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'value', 'class', 'hidden']
      });
      this.intersection = new IntersectionObserver(entries => {
        this.primaryVisible = entries[0]?.isIntersecting;
        this.refreshVisibility();
      }, { threshold: 0.25 });
      this.media = window.matchMedia('(max-width:749px)');
      this.media.addEventListener('change', () => this.refreshVisibility(), opts);
      this.resize = new ResizeObserver(() => {
        if (!this.hidden && this.getBoundingClientRect().height > 0) {
          document.documentElement.style.setProperty('--fis-sticky-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        }
      });
      this.resize.observe(this);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (String(data?.sectionId) === this.dataset.productSection) this.queueUpdate();
        });
      }
      this.update();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.intersection?.disconnect();
      this.resize?.disconnect(); this.unsubscribeVariant?.(); cancelAnimationFrame(this.frame);
      if (!document.querySelector('fis-sticky-purchase:not([hidden])')) {
        document.body.classList.remove('fis-has-sticky');
        document.documentElement.style.removeProperty('--fis-sticky-height');
      }
    }
    queueUpdate() { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); }
    selectedVariant() {
      try { return JSON.parse(this.info.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null'); }
      catch (_) { return null; }
    }
    update() {
      if (!this.isConnected || !this.info?.isConnected) return;
      const next = this.info.querySelector('.product-form__submit');
      if (next !== this.primary) {
        this.intersection.disconnect(); this.primary = next;
        if (next) this.intersection.observe(next);
      }
      if (!this.primary) {
        this.hidden = true; document.body.classList.remove('fis-has-sticky'); return;
      }
      const busy = this.primary.classList.contains('loading');
      const unavailable = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true';
      this.action.disabled = busy || unavailable;
      this.action.setAttribute('aria-busy', String(busy));
      const label = this.primary.querySelector('span:not(.loading__spinner)')?.textContent.trim();
      this.action.textContent = busy ? 'Adding...' : unavailable
        ? (label || this.dataset.unavailableLabel || 'Unavailable')
        : (this.dataset.addLabel || 'Add to cart');
      const priceBox = this.info.querySelector(`[id="price-${this.dataset.productSection}"] .price`);
      const livePrice = priceBox?.classList.contains('price--on-sale')
        ? priceBox.querySelector('.price-item--sale')
        : priceBox?.querySelector('.price__regular .price-item--regular');
      if (livePrice && this.price) this.price.textContent = livePrice.textContent.trim();
      this.updateImage(this.selectedVariant());
      this.refreshVisibility();
    }
    updateImage(variant) {
      if (this.option && variant) {
        this.option.textContent = variant.title || '';
        this.option.hidden = !variant.title || variant.title === 'Default Title';
      }
      // No 250-variant JSON array: Refresh already renders the selected variant.
      const variantImage = variant?.featured_image?.src || variant?.featured_image?.url
        || variant?.featured_media?.preview_image?.src;
      const source = variantImage || this.dataset.defaultImage;
      if (!this.image || !source) return;
      try {
        const url = new URL(source, document.baseURI);
        if (!['https:', 'http:'].includes(url.protocol)) return;
        url.searchParams.set('width', '120');
        if (this.image.getAttribute('src') !== url.href) {
          this.image.removeAttribute('srcset'); this.image.src = url.href;
        }
        this.image.alt = `${this.dataset.productTitle || ''}${variant?.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : ''}`;
        if (this.imageBox) this.imageBox.hidden = false;
      } catch (_) { /* Keep the last valid image instead of showing a broken URL. */ }
    }
    refreshVisibility() {
      const visible = !!this.primary && !this.primaryVisible && this.media?.matches;
      this.hidden = !visible;
      document.body.classList.toggle('fis-has-sticky', !!visible);
    }
    purchase() {
      if (!this.primary || this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true') return;
      // One native click preserves variant, quantity, properties, validation,
      // Shopify cart integration, analytics and error handling. No parallel cart call.
      this.primary.click();
    }
  });
  document.addEventListener('click', e => {
    const swatch=e.target.closest('[data-fis-card-swatch]');
    if(!swatch||e.ctrlKey||e.metaKey||e.shiftKey||e.altKey)return;
    const card=swatch.closest('.product-card-wrapper');if(!card)return;
    e.preventDefault();
    card.querySelectorAll('[data-fis-card-swatch]').forEach(a=>a.removeAttribute('aria-current'));
    swatch.setAttribute('aria-current','true');
    const url=swatch.getAttribute('href');
    card.querySelectorAll('a.full-unstyled-link').forEach(a=>a.setAttribute('href',url));
    const quick=card.querySelector('modal-opener [data-product-url]');if(quick)quick.dataset.productUrl=url;
    const image=card.querySelector('.card__media img');
    if(image&&swatch.dataset.image){image.removeAttribute('srcset');image.src=swatch.dataset.image;image.alt=`${card.querySelector('.card__heading')?.textContent.trim()||''} - ${swatch.dataset.label}`;card.querySelector('.card__inner')?.classList.add('fis-color-preview');}
  });
})();
